import { VideoCallProvider } from '@sawport/peers-caller';
import { ReactNode, useMemo, useCallback } from 'react';
import { useRootMemoSelector } from 'use-redux-states';
import { useDispatch } from 'react-redux';
import { setConversationId, setOtherUserId } from '../store/call';
import { createRecording } from '../utils/api/message';

interface VideoCallProviderWrapperProps {
  children: ReactNode;
}

export function VideoCallProviderWrapper({ children }: VideoCallProviderWrapperProps) {
  const dispatch = useDispatch();

  const autoRecord = useRootMemoSelector('app.company.options.autoRecord');
  // Redux selectors
  const conversationId = useRootMemoSelector('call.conversationId');
  const token = useRootMemoSelector('auth.token');
  const userObj = useRootMemoSelector('auth.user');
  const userId = useRootMemoSelector('auth.user.user.id');
  const socketUrl: string = import.meta.env.VITE_APP_SOCKET_URL;

  // Callback to get recording options when auto-recording is triggered
  const getRecordingOptions = useCallback(async () => {
    if (!conversationId) {
      throw new Error('No conversation ID available for recording');
    }

    try {
      // Call the API to create a recording entry
      const { data: recordingResponse } = await createRecording({
        conversation: conversationId
      });

      // Return recording data and config in the format expected by peers-caller
      return {
        recordingData: {
          id: recordingResponse.id,
          filename: recordingResponse.filename,
          roomId: conversationId,
          startTime: Date.now(),
          conversationId: conversationId
        }
      };
    } catch (error) {
      console.error('Failed to initialize recording:', error);
      throw error;
    }
  }, [conversationId]);

  const options = useMemo(
    () => ({
      roomId: conversationId,
      user: {
        id: String(userId || ''),
        name: userObj?.user?.name || userObj?.name || '',
        firstName: userObj?.user?.first_name || userObj?.first_name || '',
        lastName: userObj?.user?.last_name || userObj?.last_name || ''
      },
      token: token,
      socketUrl: socketUrl,
      autoRecording: autoRecord,
      getRecordingOptions: autoRecord ? getRecordingOptions : undefined,
      callbacks: {
        onCallEnded: async () => {
          // Clear call state so LiveVideo unmounts
          dispatch(setOtherUserId(undefined));
          dispatch(setConversationId(undefined));
        }
      }
    }),
    [conversationId, userId, userObj, token, socketUrl, dispatch, autoRecord, getRecordingOptions]
  );

  return <VideoCallProvider options={options}>{children}</VideoCallProvider>;
}
